import { isImageAvatar } from "@kandoo/shared";
import { LABEL_COLOR_KEYS } from "../util/labelColors";
import { useAvatar } from "./avatarMap";
import { PresetAvatar, parsePreset } from "./avatarPresets";

/** FNV-1a 32-bit hash — the identicon seed. */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Tiny deterministic PRNG (mulberry32). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const GRID = 5; // 5×5 cells, left half mirrored onto the right

/**
 * Deterministic identicon data for a user id: a mirrored cell grid plus a
 * label-palette color key. Same id → same pattern everywhere; the palette's
 * `--lc-*` variable pairs keep it legible in every theme.
 */
function identicon(userId: string): { on: boolean[][]; colorKey: string } {
  const rand = mulberry32(fnv1a(userId));
  const colorKey = LABEL_COLOR_KEYS[Math.floor(rand() * LABEL_COLOR_KEYS.length)];

  const half: boolean[][] = [];
  for (let x = 0; x < Math.ceil(GRID / 2); x++) {
    half.push(Array.from({ length: GRID }, () => rand() < 0.5));
  }
  const on: boolean[][] = [];
  let lit = 0;
  for (let x = 0; x < GRID; x++) {
    const src = half[x < GRID / 2 ? x : GRID - 1 - x];
    on.push([...src]);
    for (const cell of src) if (cell) lit++;
  }
  // very sparse hashes read as a blank dot — light up the spine
  if (lit < 6) {
    const mid = Math.floor(GRID / 2);
    for (let y = 0; y < GRID; y++) on[mid][y] = true;
  }
  return { on, colorKey };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface AvatarProps {
  userId: string;
  name: string;
  size?: "sm" | "md";
  title?: string;
  /** "identicon" (default) or the legacy initials-on-color style. */
  variant?: "identicon" | "initials";
  /**
   * Override the resolved avatar value (e.g. a live preview in the picker).
   * When omitted, the user's chosen avatar is looked up by id; pass `null` to
   * force the identicon regardless of any choice.
   */
  avatar?: string | null;
}

/**
 * A round per-user avatar. If the user has chosen a preset it's drawn; otherwise
 * a deterministic identicon (or the legacy initials style).
 */
export function Avatar({
  userId,
  name,
  size = "sm",
  title,
  variant = "identicon",
  avatar,
}: AvatarProps) {
  const sz = size === "sm" ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs";
  const resolved = useAvatar(userId);
  const value = avatar !== undefined ? avatar : resolved;

  // A chosen avatar (preset drawing or uploaded image) wins over both the
  // identicon and the legacy initials style.
  const preset = parsePreset(value);
  if (preset) {
    return (
      <span
        title={title ?? name}
        className={`inline-block shrink-0 overflow-hidden rounded-full ${sz} ring-2 ring-white`}
      >
        <PresetAvatar preset={preset} label={name} />
      </span>
    );
  }
  if (isImageAvatar(value)) {
    return (
      <span
        title={title ?? name}
        className={`inline-block shrink-0 overflow-hidden rounded-full ${sz} ring-2 ring-white`}
      >
        <img
          src={value ?? undefined}
          alt={name}
          className="h-full w-full object-cover"
        />
      </span>
    );
  }

  if (variant === "initials") {
    const colorKey = LABEL_COLOR_KEYS[fnv1a(userId) % LABEL_COLOR_KEYS.length];
    return (
      <span
        title={title ?? name}
        style={{
          backgroundColor: `var(--lc-${colorKey}-bg)`,
          color: `var(--lc-${colorKey}-text)`,
        }}
        className={`inline-flex shrink-0 items-center justify-center rounded-full ${sz} font-semibold ring-2 ring-white`}
      >
        {initials(name)}
      </span>
    );
  }

  const { on, colorKey } = identicon(userId);
  return (
    <span
      title={title ?? name}
      className={`inline-block shrink-0 overflow-hidden rounded-full ${sz} ring-2 ring-white`}
    >
      <svg
        viewBox="-0.5 -0.5 6 6"
        width="100%"
        height="100%"
        role="img"
        aria-label={name}
        shapeRendering="crispEdges"
        data-identicon={userId}
      >
        <rect
          x="-0.5"
          y="-0.5"
          width="6"
          height="6"
          fill={`var(--lc-${colorKey}-bg)`}
        />
        {on.flatMap((col, x) =>
          col.map((cell, y) =>
            cell ? (
              <rect
                key={`${x}-${y}`}
                x={x}
                y={y}
                width="1"
                height="1"
                fill={`var(--lc-${colorKey}-text)`}
              />
            ) : null,
          ),
        )}
      </svg>
    </span>
  );
}
